"use client";

import { useEffect, useState } from "react";
import { onConsentCategory, watchConsentCategory } from "./index";

type PianoMode = "opt-in" | "opt-out" | "essential";

type FacebookPixel = ((...args: Array<unknown>) => void) & {
  callMethod?: (...args: Array<unknown>) => void;
  loaded: boolean;
  push: FacebookPixel;
  queue: Array<Array<unknown>>;
  version: string;
};

type OpenAIPixel = ((...args: Array<unknown>) => void) & {
  q?: Array<Array<unknown>>;
};

type ConsentRuntime = Window & {
  _fbq?: FacebookPixel;
  _pac?: {
    collectDomain?: string;
    instantTracking?: {
      downloads: boolean;
      pages: boolean;
      searches: boolean;
    };
    site?: string;
  };
  _paq?: Array<unknown>;
  dataLayer?: Array<unknown>;
  fbq?: FacebookPixel;
  oaiq?: OpenAIPixel;
  pa?: {
    consent?: {
      setMode: (mode: PianoMode) => void;
    };
  };
  pdl?: {
    consent?: {
      defaultPreset: {
        PA: PianoMode;
      };
    };
    requireConsent?: boolean;
  };
};

const GTM_ID = "GTM-NRM25QT";
const FACEBOOK_PIXEL_ID = "2231018863590581";
const OPENAI_PIXEL_ID = "CxYWqPiEnXVv4iLwvZ3qMH";
const PIANO_STORAGE_NAMES = ["_cookie_test", "_pcid", "_pctx", "_pprv", "__test_localStorage__"];

const SERVICE_SCRIPTS = {
  iadvize: {
    category: "4",
    src: "https://halc.iadvize.com/iadvize.js?sid=7333",
  },
  selligent: {
    category: "4",
    src: "https://intermedes.slgnt.eu/optiext/webtracker.dll",
  },
  trustpilot: {
    category: "2",
    src: "https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js",
  },
} as const;

export type ConsentService = keyof typeof SERVICE_SCRIPTS;

type ConsentScriptsProps = {
  pianoCollectDomain: string;
  pianoSiteId: string;
  services?: ReadonlyArray<ConsentService>;
};

type ConsentPixelProps = {
  src: string;
  consentCategory?: string;
  width?: number;
  height?: number;
  alt?: string;
};

type TradeDoublerConfig = Record<string, string | number | boolean>;

type TradeDoublerTagProps = {
  containerTagId: string;
  config?: TradeDoublerConfig;
  consentCategory?: string;
};

declare global {
  interface Window {
    TDConf?: {
      Config?: TradeDoublerConfig & { containerTagId: string };
      sudomain?: string;
      host?: string;
      containerTagURL?: string;
    };
  }
}

let facebookLoaded = false;
let gtmLoaded = false;
let openAiLoaded = false;
let pianoLoaded = false;
let consentReloadScheduled = false;
let tcConsentAllowedSent = false;

const loadExternalScript = (id: string, src: string, onLoad?: () => void) => {
  const existingScript = document.querySelector<HTMLScriptElement>(`script[data-consent-script="${id}"]`);
  if (existingScript) {
    if (existingScript.dataset.loaded === "true") {
      onLoad?.();
    } else if (onLoad) {
      existingScript.addEventListener("load", onLoad, { once: true });
    }
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  script.dataset.consentScript = id;
  script.addEventListener(
    "load",
    () => {
      script.dataset.loaded = "true";
      onLoad?.();
    },
    { once: true },
  );
  document.head.appendChild(script);
};

const cookieDomains = () => {
  const hostname = window.location.hostname;
  const rootDomain = hostname.split(".").slice(-2).join(".");
  return Array.from(new Set([undefined, hostname, `.${hostname}`, rootDomain, `.${rootDomain}`]));
};

const deleteCookie = (name: string) => {
  for (const domain of cookieDomains()) {
    const domainAttribute = domain ? `; Domain=${domain}` : "";
    document.cookie = `${name}=; Max-Age=0; Path=/${domainAttribute}; SameSite=Lax`;
  }
};

const clearStorage = (names: Array<string>, prefixes: Array<string> = []) => {
  for (const name of names) deleteCookie(name);

  for (const getStorage of [() => localStorage, () => sessionStorage]) {
    try {
      const storage = getStorage();
      for (const key of Object.keys(storage)) {
        if (names.includes(key) || prefixes.some((prefix) => key.startsWith(prefix))) {
          storage.removeItem(key);
        }
      }
    } catch {
      // Storage can be unavailable in hardened browser contexts.
    }
  }
};

const cookieNamesStartingWith = (prefixes: ReadonlyArray<string>) =>
  document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter((name): name is string => !!name && prefixes.some((prefix) => name.startsWith(prefix)));

const scheduleConsentReload = () => {
  if (consentReloadScheduled) return;
  consentReloadScheduled = true;
  // Removing a script element does not stop code that has already executed.
  // Reload after revocation so advertising runtimes cannot keep running in memory.
  window.setTimeout(() => window.location.reload(), 0);
};

const consentRuntime = () => window as ConsentRuntime;

// TrustCommander is the single source of truth for Google Consent Mode defaults and updates.
const loadGtm = () => {
  if (gtmLoaded) return;
  gtmLoaded = true;

  const runtime = consentRuntime();
  runtime.dataLayer = runtime.dataLayer || [];
  runtime.dataLayer?.push({ event: "gtm.js", "gtm.start": Date.now() });
  loadExternalScript("gtm", `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
};

const disableGoogleAdvertising = () => {
  clearStorage(cookieNamesStartingWith(["_gcl_"]), ["_gcl_"]);
};

const disableFacebook = () => {
  consentRuntime().fbq?.("consent", "revoke");
  clearStorage(cookieNamesStartingWith(["_fb"]), ["_fb"]);
};

const loadFacebook = () => {
  const runtime = consentRuntime();
  if (facebookLoaded) {
    runtime.fbq?.("consent", "grant");
    return;
  }

  facebookLoaded = true;
  const fbq = ((...args: Array<unknown>) => {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  }) as FacebookPixel;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  runtime.fbq = fbq;
  runtime._fbq = fbq;
  loadExternalScript("facebook", "https://connect.facebook.net/en_US/fbevents.js");
  runtime.fbq("consent", "grant");
  runtime.fbq("init", FACEBOOK_PIXEL_ID);
  runtime.fbq("track", "PageView");
};

const loadOpenAI = () => {
  const runtime = consentRuntime();
  if (openAiLoaded) {
    runtime.oaiq?.("consent", true);
    return;
  }

  openAiLoaded = true;
  if (!runtime.oaiq) {
    const oaiq = ((...args: Array<unknown>) => {
      oaiq.q?.push(args);
    }) as OpenAIPixel;
    oaiq.q = [];
    runtime.oaiq = oaiq;
    loadExternalScript("openai", "https://bzrcdn.openai.com/sdk/oaiq.min.js");
  }

  runtime.oaiq("consent", true);
  runtime.oaiq("init", { pixelId: OPENAI_PIXEL_ID, debug: process.env.NODE_ENV !== "production" });
};

const configureAndLoadPiano = (pianoCollectDomain: string, pianoSiteId: string) => {
  const runtime = consentRuntime();
  if (pianoLoaded) {
    runtime.pa?.consent?.setMode("opt-in");
    return;
  }

  pianoLoaded = true;
  runtime._paq = [];
  runtime.pdl = {
    ...runtime.pdl,
    consent: { defaultPreset: { PA: "opt-in" } },
    requireConsent: true,
  };
  runtime._pac = {
    ...runtime._pac,
    collectDomain: pianoCollectDomain,
    instantTracking: {
      downloads: false,
      pages: true,
      searches: false,
    },
    site: pianoSiteId,
  };

  loadExternalScript("piano-analytics", "https://tag.aticdn.net/piano-analytics.js", () => {
    runtime.pa?.consent?.setMode("opt-in");
  });
};

const disablePiano = () => {
  const runtime = consentRuntime();
  runtime.pa?.consent?.setMode("essential");
  runtime._paq = [];
  clearStorage(PIANO_STORAGE_NAMES);
};

export function ConsentApiStub() {
  return (
    <script
      // biome-ignore lint/security/noDangerouslySetInnerHtml: this static stub queues TrustCommander calls before its SDK loads.
      dangerouslySetInnerHTML={{
        __html:
          "window.caReady = window.caReady || []; window.cact = window.cact || function() { window.caReady.push(arguments); };",
      }}
    />
  );
}

export function ConsentScripts({ pianoCollectDomain, pianoSiteId, services = [] }: ConsentScriptsProps) {
  useEffect(() => {
    const stopWatchingAnalytics = watchConsentCategory("2", (allowed) => {
      if (!allowed) {
        disablePiano();
        return;
      }

      configureAndLoadPiano(pianoCollectDomain, pianoSiteId);
      if (!tcConsentAllowedSent) {
        tcConsentAllowedSent = true;
        consentRuntime().dataLayer?.push({ event: "tcConsentAllowed" });
      }
    });

    const stopWatchingAdvertising = watchConsentCategory("3", (allowed) => {
      if (!allowed) {
        const advertisingScriptsWereLoaded = gtmLoaded || facebookLoaded || openAiLoaded;
        disableFacebook();
        consentRuntime().oaiq?.("consent", false);
        disableGoogleAdvertising();
        if (advertisingScriptsWereLoaded) scheduleConsentReload();
        return;
      }

      loadGtm();
      loadFacebook();
      loadOpenAI();
    });

    const stopWatchingServices = services.map((service) => {
      const config = SERVICE_SCRIPTS[service];
      return onConsentCategory(config.category, () => loadExternalScript(service, config.src));
    });

    return () => {
      stopWatchingAnalytics();
      stopWatchingAdvertising();
      for (const stopWatching of stopWatchingServices) stopWatching();
    };
  }, [pianoCollectDomain, pianoSiteId, services]);

  return null;
}

export const ConsentPixel = ({ src, consentCategory = "2", width = 1, height = 1, alt = "" }: ConsentPixelProps) => {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => watchConsentCategory(consentCategory, setAllowed), [consentCategory]);

  if (!allowed) return null;

  return <img src={src} width={width} height={height} alt={alt} />;
};

export const TradeDoublerTag = ({ containerTagId, config = {}, consentCategory = "2" }: TradeDoublerTagProps) => {
  useEffect(
    () =>
      onConsentCategory(consentCategory, () => {
        const scriptId = `tradedoubler-${containerTagId}`;
        if (document.querySelector(`script[data-tradedoubler-tag="${scriptId}"]`)) return;

        window.TDConf = window.TDConf || {};
        window.TDConf.Config = {
          protocol: document.location.protocol,
          ...config,
          containerTagId,
        };
        window.TDConf.sudomain = document.location.protocol === "https:" ? "swrap" : "wrap";
        window.TDConf.host = ".tradedoubler.com/wrap";
        window.TDConf.containerTagURL = `${document.location.protocol}//${window.TDConf.sudomain}${window.TDConf.host}`;

        const script = document.createElement("script");
        script.async = true;
        script.defer = true;
        script.src = `${window.TDConf.containerTagURL}?id=${containerTagId}`;
        script.dataset.tradedoublerTag = scriptId;
        document.body.appendChild(script);
      }),
    [config, consentCategory, containerTagId],
  );

  return null;
};
